import cs from "classnames";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import { getName as getCountryName } from "i18n-iso-countries";
import React from "react";
import ReactCountryFlag from "react-country-flag";
import { FaChevronDown } from "react-icons/fa";
import {
	defineMessages,
	FormattedMessage,
	injectIntl,
	WrappedComponentProps as IntlProps
} from "react-intl";

import { SearchMenu } from "src/common/components/SearchMenu";
import { normalizeSearchString } from "src/common/utils/common";

import "src/common/components/Input/Input.css";
import "./PhoneNumberInput.css";

const phoneNumberUtil = PhoneNumberUtil.getInstance();
const supportedRegions = phoneNumberUtil.getSupportedRegions();

const messages = defineMessages({
	countryNotFound: {
		id: "phone_number_input.country_not_found",
		defaultMessage: "A keresett ország nem található"
	}
});

type PhoneNumberInputProps = IntlProps & InputProps & InnerProps;

type InputProps = Omit<
	React.HTMLProps<HTMLInputElement>,
	"type" | "value" | "onChange"
>;

interface InnerProps {
	decorator?: React.ReactNode;
	defaultRegion?: string;
	value?: string | undefined;
	onChangeValue?(value: string | undefined): unknown;
}

interface PhoneNumberInputState {
	translatedRegions: TranslatedRegion[];
	isFocused: boolean;
	isMenuOpen: boolean;
	value: string | undefined;
	region: string;
	number: string;
}

interface TranslatedRegion {
	code: string;
	englishName: string;
	localName: string;
}

class BarePhoneNumberInput extends React.PureComponent<
	PhoneNumberInputProps,
	PhoneNumberInputState
> {
	static defaultProps = { defaultRegion: "HU" };
	state = {
		translatedRegions: [],
		isFocused: false,
		isMenuOpen: false,
		value: undefined,
		region: "",
		number: ""
	};

	private numberRef = React.createRef<HTMLInputElement>();

	componentDidMount(): void {
		this.translateRegions();
		this.processInitialValue();
	}

	componentDidUpdate(prevProps: PhoneNumberInputProps): void {
		if (this.props.intl.locale !== prevProps.intl.locale) {
			this.translateRegions();
		}
		if (
			this.props.value !== prevProps.value &&
			this.props.value !== this.state.value
		) {
			this.processValue();
		}
	}

	render(): React.ReactNode {
		const { className, disabled } = this.props;
		const { isFocused } = this.state;
		return (
			<div className={cs("phone-number-input", className)}>
				<div
					className={cs("input", {
						"input--disabled": disabled,
						"input--focused": isFocused
					})}
				>
					{this.renderRegionButton()}
					{this.renderCountryCode()}
					{this.renderNumberInput()}
					{this.renderDecorator()}
				</div>
				{this.renderRegionMenu()}
			</div>
		);
	}

	private renderRegionButton(): React.ReactElement {
		const { disabled } = this.props;
		return (
			<button
				type="button"
				className="region-button"
				disabled={disabled}
				onFocus={this.onFocus}
				onBlur={this.onBlur}
				onClick={this.onOpenMenu}
			>
				<div className="country-flag">{this.renderCountryFlag()}</div>
				<FaChevronDown />
			</button>
		);
	}

	private renderCountryFlag(): React.ReactElement | undefined {
		const { region } = this.state;
		if (region !== "") {
			return <ReactCountryFlag svg={true} countryCode={region} />;
		}
	}

	private renderCountryCode(): React.ReactElement | undefined {
		const { region } = this.state;
		if (region !== "") {
			const code = phoneNumberUtil.getCountryCodeForRegion(region);
			return <div className="country-code">+{code}</div>;
		}
	}

	private renderNumberInput(): React.ReactElement {
		const {
			className,
			decorator,
			defaultRegion,
			value,
			onChangeValue,
			...inputProps
		} = this.props;
		const { number } = this.state;
		return (
			<input
				{...inputProps}
				ref={this.numberRef}
				type="tel"
				onFocus={this.onFocusNumber}
				onBlur={this.onBlurNumber}
				value={number}
				onChange={this.onChangeNumber}
			/>
		);
	}

	private renderDecorator(): React.ReactElement | undefined {
		const { decorator } = this.props;
		if (decorator) {
			return <div className="input__decorator">{decorator}</div>;
		}
	}

	private renderRegionMenu(): React.ReactElement {
		const { isMenuOpen } = this.state;
		return (
			<SearchMenu
				isMenuOpen={isMenuOpen}
				onCloseMenu={this.onCloseMenu}
				items={this.state.translatedRegions}
				noItemsMessage={<CountryNotFoundMessage />}
				itemPredicate={regionPredicate}
				getItemKey={getRegionKey}
				renderItem={renderRegion}
				onSelectItem={this.onSelectRegion}
			/>
		);
	}

	private translateRegions(): void {
		this.setState({
			translatedRegions: translateRegions(
				supportedRegions,
				this.props.intl.locale
			)
		});
	}

	private processInitialValue(): void {
		const { defaultRegion, value } = this.props;
		if (value !== undefined && value !== "") {
			const [region, number] = splitValue(value);
			this.setState({ value, region, number });
		} else {
			this.setState({ region: defaultRegion || "" });
		}
	}

	private processValue(): void {
		const { defaultRegion, value } = this.props;
		if (value !== undefined && value !== "") {
			const [region, number] = splitValue(value);
			this.setState({
				value,
				region,
				number
			});
		} else {
			this.setState({
				value: "",
				region: defaultRegion || "",
				number: ""
			});
		}
	}

	private onFocus = (): void => {
		this.setState({ isFocused: true });
	};

	private onBlur = (): void => {
		this.setState({ isFocused: false });
	};

	private onOpenMenu = (): void => {
		this.setState({ isMenuOpen: true });
	};

	private onCloseMenu = (): void => {
		this.setState({ isMenuOpen: false });
	};

	private onSelectRegion = ({ code }: TranslatedRegion): void => {
		this.setRegion(code);
		this.onCloseMenu();
		if (this.numberRef.current !== null) {
			this.numberRef.current.focus();
		}
	};

	private onFocusNumber = (
		event: React.FocusEvent<HTMLInputElement>
	): void => {
		this.onFocus();
		if (this.props.onFocus) {
			this.props.onFocus(event);
		}
	};

	private onBlurNumber = (
		event: React.FocusEvent<HTMLInputElement>
	): void => {
		this.onBlur();
		if (this.props.onBlur) {
			this.props.onBlur(event);
		}
	};

	private onChangeNumber = (
		event: React.ChangeEvent<HTMLInputElement>
	): void => {
		this.setNumber(event.target.value);
	};

	private setRegion(region: string): void {
		this.setState(
			({ number }) => ({
				value: joinValue(region, number),
				region,
				number
			}),
			this.emitOnChangeValue
		);
	}

	private setNumber(number: string): void {
		this.setState(
			({ region }) => ({
				value: joinValue(region, number),
				region,
				number
			}),
			this.emitOnChangeValue
		);
	}

	private emitOnChangeValue = (): void => {
		if (this.props.onChangeValue) {
			this.props.onChangeValue(this.state.value);
		}
	};
}

export const PhoneNumberInput = injectIntl(BarePhoneNumberInput);

function translateRegions(
	regions: string[],
	locale: string
): TranslatedRegion[] {
	return regions
		.map(code => ({
			code,
			englishName: getCountryName(code, "EN") ?? "",
			localName: getCountryName(code, locale) ?? ""
		}))
		.filter(region => region.localName !== undefined);
}

function splitValue(value: string): readonly [string, string] {
	let region = "";
	let number = "";
	if (value !== "") {
		try {
			const phoneNumber = phoneNumberUtil.parse(value);
			const code = phoneNumber.getCountryCode();
			if (code !== undefined) {
				region = phoneNumberUtil.getRegionCodeForCountryCode(code);
			}
			number = value.replace(new RegExp(`^\\+?${code}`), "").trim();
		} catch {
			// ignore errors
		}
	}
	return [region, number];
}

function joinValue(region: string, number: string): string | undefined {
	if (region === "" || number === "") {
		return undefined;
	}
	const code = phoneNumberUtil.getCountryCodeForRegion(region);
	let value = `+${code} ${number}`;
	try {
		const phoneNumber = phoneNumberUtil.parse(value);
		value = phoneNumberUtil.format(
			phoneNumber,
			PhoneNumberFormat.INTERNATIONAL
		);
	} catch {
		// ignore errors
	}
	return value;
}

function CountryNotFoundMessage(): React.ReactElement {
	return (
		<div className="region-list-item">
			<FormattedMessage {...messages.countryNotFound} />
		</div>
	);
}

function regionPredicate(
	{ code, localName, englishName }: TranslatedRegion,
	query: string
): boolean {
	const normalizedQuery = normalizeSearchString(query);
	return (
		normalizeSearchString(code).includes(normalizedQuery) ||
		normalizeSearchString(localName).includes(normalizedQuery) ||
		normalizeSearchString(englishName).includes(normalizedQuery)
	);
}

function getRegionKey({ code }: TranslatedRegion): string {
	return code;
}

function renderRegion({
	code,
	localName
}: TranslatedRegion): React.ReactElement {
	return (
		<div className="region-list-item">
			<div className="country-flag">
				<ReactCountryFlag svg={true} countryCode={code} />
			</div>
			{localName}
		</div>
	);
}
